/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component, createContext } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { withEuiTheme, keys, htmlIdGenerator } from '../../services';
import { EuiI18n } from '../i18n';
import { EuiScreenReaderOnly } from '../accessibility';
import { EuiTreeViewItem } from './tree_view_item';
import { euiTreeViewStyles } from './tree_view.styles';
var EuiTreeViewContext = /*#__PURE__*/createContext('');
function getTreeId(propId) {
  var contextId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var idGenerator = arguments.length > 2 ? arguments[2] : undefined;
  return propId !== null && propId !== void 0 ? propId : contextId === '' ? idGenerator() : contextId;
}
export class EuiTreeViewClass extends Component {
  treeIdGenerator = htmlIdGenerator('euiTreeView');
  static contextType = EuiTreeViewContext;
  isNested;
  constructor(props,
  // Without the optional ? typing, TS will throw errors on JSX component errors
  // @see https://github.com/facebook/react/issues/13944#issuecomment-1183693239
  context) {
    // TODO: context in constructor has been deprecated.
    // We should likely convert this to a function component
    super(props, context);
    this.isNested = !!this.context;
    this.state = {
      openItems: this.props.expandByDefault ? this.props.items.map(({
        id,
        children
      }) => children ? id : null).filter(x => x != null) : this.props.items.map(({
        id,
        children,
        isExpanded
      }) => children && isExpanded ? id : null).filter(x => x != null),
      activeItem: '',
      treeID: getTreeId(this.props.id, context, this.treeIdGenerator),
      expandChildNodes: this.props.expandByDefault || false
    };
  }
  componentDidUpdate(prevProps) {
    if (this.props.id !== prevProps.id) {
      this.setState({
        treeID: getTreeId(this.props.id, this.context, this.treeIdGenerator)
      });
    }
  }
  buttonRef = [];
  setButtonRef = (ref, index) => {
    this.buttonRef[index] = ref;
  };
  handleNodeClick = (node, ignoreCallback = false) => {
    const index = this.state.openItems.indexOf(node.id);
    this.setState({
      expandChildNodes: false
    });
    node.isExpanded = !node.isExpanded;
    if (!ignoreCallback && node.callback !== undefined) {
      node.callback();
    }
    if (this.isNodeOpen(node)) {
      // if the node is part of openItems[] then remove it
      this.setState({
        openItems: this.state.openItems.filter((_, i) => i !== index)
      });
    } else {
      // if the node isn't part of openItems[] then add it
      this.setState(prevState => ({
        openItems: [...prevState.openItems, node.id],
        activeItem: node.id
      }));
    }
  };

  // check if the node is included in openItems[]
  isNodeOpen = node => {
    return this.state.openItems.includes(node.id);
  };

  // Enable keyboard navigation
  onKeyDown = (event, node) => {
    switch (event.key) {
      case keys.ARROW_DOWN:
        {
          const nodeButtons = Array.from(document.querySelectorAll(`[data-test-subj="euiTreeViewButton-${this.state.treeID}"]`));
          const currentIndex = nodeButtons.indexOf(event.currentTarget);
          if (currentIndex > -1) {
            const nextButton = nodeButtons[currentIndex + 1];
            if (nextButton) {
              event.preventDefault();
              event.stopPropagation();
              nextButton.focus();
            }
          }
          break;
        }
      case keys.ARROW_UP:
        {
          const nodeButtons = Array.from(document.querySelectorAll(`[data-test-subj="euiTreeViewButton-${this.state.treeID}"]`));
          const currentIndex = nodeButtons.indexOf(event.currentTarget);
          if (currentIndex > -1) {
            const prevButton = nodeButtons[currentIndex + -1];
            if (prevButton) {
              event.preventDefault();
              event.stopPropagation();
              prevButton.focus();
            }
          }
          break;
        }
      case keys.ARROW_RIGHT:
        {
          if (!this.isNodeOpen(node)) {
            event.preventDefault();
            event.stopPropagation();
            this.handleNodeClick(node, true);
          }
          break;
        }
      case keys.ARROW_LEFT:
        {
          if (this.isNodeOpen(node)) {
            event.preventDefault();
            event.stopPropagation();
            this.handleNodeClick(node, true);
          }
        }
      default:
        break;
    }
  };
  onChildrenKeydown = (event, index) => {
    if (event.key === keys.ARROW_LEFT) {
      event.preventDefault();
      event.stopPropagation();
      this.buttonRef[index].focus();
    }
  };
  render() {
    const {
      children,
      className,
      items,
      display = 'default',
      expandByDefault,
      showExpansionArrows,
      theme,
      ...rest
    } = this.props;
    const styles = euiTreeViewStyles(theme);
    const cssStyles = [styles.euiTreeView, styles[display]];

    // Computed classNames
    const classes = classNames('euiTreeView', className);
    const instructionsId = `${this.state.treeID}--instruction`;
    return <EuiTreeViewContext.Provider value={this.state.treeID}>
        {!this.isNested && <EuiI18n token="euiTreeView.listNavigationInstructions" default="You can quickly navigate this list using arrow keys.">
            {listNavigationInstructions => <EuiScreenReaderOnly>
                <p id={instructionsId}>{listNavigationInstructions}</p>
              </EuiScreenReaderOnly>}
          </EuiI18n>}
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
        <ul css={cssStyles} className={classes} id={!this.isNested ? this.state.treeID : undefined} aria-describedby={!this.isNested ? instructionsId : undefined} role="list" // VoiceOver doesn't parse lists with `list-style: none` as the correct role - @see https://www.scottohara.me/blog/2019/01/12/lists-and-safari.html
      {...rest}>
          {items.map((node, index) => {
          const buttonId = node.id;
          const wrappingId = this.treeIdGenerator(buttonId);
          const isNodeExpanded = node.children ? this.isNodeOpen(node) : undefined; // Determines the `aria-expanded` attribute

          let icon = node.icon;
          if (node.iconWhenExpanded && isNodeExpanded) {
            icon = node.iconWhenExpanded;
          } else if (!icon && node.useEmptyIcon) {
            icon = <></>; // Renders a placeholder
          }

          return <EuiTreeViewItem key={buttonId + index} id={buttonId} className={node.className} buttonRef={ref => this.setButtonRef(ref, index)} aria-controls={node.children ? wrappingId : undefined} label={node.label} icon={icon} hasArrow={showExpansionArrows} isExpanded={isNodeExpanded} isActive={this.state.activeItem === node.id} display={display} data-test-subj={`euiTreeViewButton-${this.state.treeID}`} onKeyDown={event => this.onKeyDown(event, node)} onClick={() => this.handleNodeClick(node)}>
                {node.children && <div id={wrappingId} onKeyDown={event => this.onChildrenKeydown(event, index)}>
                    {isNodeExpanded && <EuiTreeView items={node.children} display={display} showExpansionArrows={showExpansionArrows} expandByDefault={this.state.expandChildNodes} />}
                  </div>}
              </EuiTreeViewItem>;
        })}
        </ul>
      </EuiTreeViewContext.Provider>;
  }
}
EuiTreeViewClass.propTypes = {
  className: PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * An array of EuiTreeViewNodes
       */
  items: PropTypes.arrayOf(PropTypes.shape({
    /** An array of EuiTreeViewNodes to render as children
       */
    children: PropTypes.arrayOf(PropTypes.any.isRequired),
    /** The readable label for the item
       */
    label: PropTypes.node.isRequired,
    /** A unique ID
       */
    id: PropTypes.string.isRequired,
    /** An icon to use on the left of the label
       */
    icon: PropTypes.element,
    /** Display a different icon when the item is expanded.
      For instance, an open folder or a down arrow
      */
    iconWhenExpanded: PropTypes.element,
    /** Use an empty icon to keep items without an icon
      lined up with their siblings
      */
    useEmptyIcon: PropTypes.bool,
    /** Whether or not the item is expanded.
       */
    isExpanded: PropTypes.bool,
    /** Optional class to throw on the node
       */
    className: PropTypes.string,
    /** Function to call when the item is clicked.
       The open state of the item will always be toggled.
       */
    callback: PropTypes.func
  }).isRequired).isRequired,
  /**
       * Optionally use a variation with smaller text and icon sizes
       * @default default
       */
  display: PropTypes.oneOf(["default", "compressed"]),
  /**
       * Set all items to open on initial load
       */
  expandByDefault: PropTypes.bool,
  /**
       * Display expansion arrows next to all items
       * that contain children
       */
  showExpansionArrows: PropTypes.bool,
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string
};
export const EuiTreeView = Object.assign(withEuiTheme(EuiTreeViewClass), {
  Item: EuiTreeViewItem
});
try {
  EuiTreeViewClass.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/tree_view/tree_view.tsx',
    description: '',
    displayName: 'EuiTreeViewClass',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: 'Never accepts children directly, only through the `items` prop',
        name: 'children',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      display: {
        defaultValue: {
          value: 'default'
        },
        description: 'Optionally use a variation with smaller text and icon sizes',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiTreeViewDisplayOptions',
          value: [{
            value: '"default"'
          }, {
            value: '"compressed"'
          }]
        }
      },
      items: {
        defaultValue: null,
        description: 'An array of EuiTreeViewNodes',
        name: 'items',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'Node[]'
        }
      },
      expandByDefault: {
        defaultValue: null,
        description: 'Set all items to open on initial load',
        name: 'expandByDefault',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showExpansionArrows: {
        defaultValue: null,
        description: 'Display expansion arrows next to all items\nthat contain children',
        name: 'showExpansionArrows',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      theme: {
        defaultValue: null,
        description: '',
        name: 'theme',
        parent: {
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        },
        declarations: [{
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        }, {
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        }],
        required: true,
        type: {
          name: 'UseEuiTheme<{}>'
        }
      },
      'aria-labelledby': {
        defaultValue: null,
        description: '',
        name: 'aria-labelledby',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'string'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}
try {
  EuiTreeView.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/tree_view/tree_view.tsx',
    description: '',
    displayName: 'EuiTreeView',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      children: {
        defaultValue: null,
        description: 'Never accepts children directly, only through the `items` prop',
        name: 'children',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'never'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      display: {
        defaultValue: {
          value: 'default'
        },
        description: 'Optionally use a variation with smaller text and icon sizes',
        name: 'display',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiTreeViewDisplayOptions',
          value: [{
            value: '"default"'
          }, {
            value: '"compressed"'
          }]
        }
      },
      items: {
        defaultValue: null,
        description: 'An array of EuiTreeViewNodes',
        name: 'items',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'Node[]'
        }
      },
      expandByDefault: {
        defaultValue: null,
        description: 'Set all items to open on initial load',
        name: 'expandByDefault',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showExpansionArrows: {
        defaultValue: null,
        description: 'Display expansion arrows next to all items\nthat contain children',
        name: 'showExpansionArrows',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/tree_view/tree_view.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null` (or call the ref with `null` if you passed a callback ref).\n' + '@see https://react.dev/learn/referencing-values-with-refs#refs-and-the-dom',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps', 'WithEuiThemeProps', 'Attributes']
  };
} catch (e) {}